import {
  ComboBox,
  DataStateChangeEvent,
  PromptElementController,
  CheckBoxList,
  TextField,
  RangeSliderController,
  DualSlider,
  RadioBoxList,
  LinkBar,
  PositionedModelController,
} from '@sas-dvr/ltjs-bird';
import { from, merge, of } from 'rxjs';
import { fromPortableEvent } from '../util';
import { VisualElementControllerLoader } from './LoaderFactory';
import { ComboBoxImpl } from './promptElementImpl/ComboBoxImpl';
import { PromptElementImplInterface } from './promptElementImpl/PromptElementImplInterface';
import { UnknownImpl } from './promptElementImpl/UnknownImpl';
import { CheckboxListImpl } from './promptElementImpl/CheckboxListImpl';
import { TextFieldImpl } from './promptElementImpl/TextFieldImpl';
import { SliderImpl } from './promptElementImpl/SliderImpl';
import { ButtonBarImpl } from './promptElementImpl/ButtonBarImpl';
import { RadioListImpl } from './promptElementImpl/RadioListImpl';
import { getPromptStyleOverrides } from './promptElementImpl/utils';
import { StyleState } from '@sas-dvr/internal-va-react-core/store/style/StyleState';
import { loadDataVisualElementController } from './loadDataVisualElement';
import { DimensionUnit, FlexibilityType } from '@sas-dvr/ltjs-commons';
import {
  BirdLayoutState,
  PromptConstraints,
} from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';

function createSpecialization(
  controller: PromptElementController,
  id: string
): PromptElementImplInterface {
  const model = controller.getModel();
  if (!model) {
    throw new Error(`Unable to get model for PromptElementController ${id}`);
  }
  const specialization = model.getSpecialization();
  if (!specialization) {
    throw new Error(`Unable to get specialization for PromptElementController ${id}`);
  }

  if (specialization instanceof ComboBox) {
    return new ComboBoxImpl(controller, id);
  } else if (specialization instanceof CheckBoxList) {
    return new CheckboxListImpl(controller, id, specialization);
  } else if (specialization instanceof RadioBoxList) {
    return new RadioListImpl(controller, id, specialization);
  } else if (specialization instanceof TextField) {
    return new TextFieldImpl(controller, id);
  } else if (controller instanceof RangeSliderController) {
    return new SliderImpl(controller, id, specialization instanceof DualSlider);
  } else if (specialization instanceof LinkBar) {
    return new ButtonBarImpl(controller, id, specialization);
  }
  return new UnknownImpl(id);
}

export const loadPromptElementController: VisualElementControllerLoader = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(visualElementController instanceof PromptElementController)) {
    throw new Error('Expected the controller to be a PromptElementController');
  }
  const id = deps.getKey(visualElementController);
  const specialization = createSpecialization(visualElementController, id);

  const overrides = getPromptStyleOverrides(visualElementController);
  const style = specialization.modifyStyle ? specialization.modifyStyle(overrides) : overrides;

  const promptConstraintActions = [];
  if (visualController) {
    const promptConstraints = getPromptLayoutConstraints(visualController);
    if (promptConstraints) {
      promptConstraintActions.push(
        BirdLayoutState.actions.setPromptConstraints(id, promptConstraints)
      );
    }
  }

  return merge(
    //
    // Set prompt constraints, used in prompt headers and prompt containers
    //
    from(promptConstraintActions),

    //
    // Set style overrides
    //
    of(StyleState.actions.set(id, style)),

    //
    // VisualElement load
    //
    loadDataVisualElementController(
      visualController,
      visualElementController,
      unload$,
      myActions$,
      state$,
      deps,
      {
        create: () => specialization.createElement(),
        delete: () => specialization.deleteElement(),
      }
    ),
    //
    // Respond to DATACHANGE events
    //
    specialization.handleDataChange(
      fromPortableEvent<DataStateChangeEvent>(
        visualElementController,
        DataStateChangeEvent.DATACHANGE
      )
    ),
    //
    // Respond to CURRENT_VALUE_CHANGED events
    //
    specialization.handleValueChanged(
      fromPortableEvent(visualElementController, PromptElementController.CURRENT_VALUE_CHANGED)
    ),
    //
    // Respond to prompt specific user actions.
    //
    specialization.handleActions(myActions$, state$)
  );
};

export function getPromptLayoutConstraints(
  visualController: PositionedModelController
): PromptConstraints | null {
  const constraint = visualController.getLayoutProxy()?.getConstraint();
  if (!constraint) {
    return null;
  }
  const promptLayout: PromptConstraints = {
    widthFlexibility: convertFlexibility(constraint.getWidthFlexibility()),
    heightFlexibility: convertFlexibility(constraint.getHeightFlexibility()),
  };
  const width = constraint.getWidth();
  if (width) {
    if (width.getUnit() !== DimensionUnit.PERCENTAGE) {
      console.warn('Unexpected dimension unit for prompt header prompt: ', width?.getUnit());
    } else {
      promptLayout.widthConstraint = width.getValue() + '%';
    }
  }
  const height = constraint.getHeight();
  if (height) {
    if (height.getUnit() !== DimensionUnit.PERCENTAGE) {
      console.warn('Unexpected dimension unit for prompt header prompt: ', width?.getUnit());
    } else {
      promptLayout.heightConstraint = height.getValue() + '%';
    }
  }
  return promptLayout;
}

function convertFlexibility(flexibility: FlexibilityType) {
  switch (flexibility) {
    case FlexibilityType.FLEXIBLE:
      return 'flexible';
    case FlexibilityType.GROWABLE:
      return 'growable';
    case FlexibilityType.SHRINKABLE:
      return 'shrinkable';
    case FlexibilityType.FIXED:
      return 'fixed';
  }
}
